import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import cx from 'classnames';
import PropTypes from 'prop-types';

import ReactVimeo from 'components/consumer/ReactVimeo';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import DesktopColumnsMobileStack from 'components/consumer/DesktopColumnsMobileStack/DesktopColumnsMobileStack';

const SingleHeroVideoWistia = ({
  autoplay,
  isAdmin,
  videoId = 'kwykvxwggps',
}) => {
  const [isImageThumbnailLoaded, setIsImageThumbnailLoaded] = useState(false);

  if (isAdmin) {
    return (
      <div className="min-h-[596px] ">
        JB Dual Hero Video Placeholder for Variant Builder. <br />
        The component will display correctly in Consumer
      </div>
    );
  }

  return (
    !!videoId && (
      <>
        <Helmet>
          <script
            src={`//fast.wistia.com/embed/medias/${videoId}.jsonp`}
            async
          />

          <script src="//fast.wistia.com/assets/external/E-v1.js" async />
        </Helmet>

        <div className="DualHeroVideoCore">
          <div className="px-0 py-[56.25%0] relative">
            <div className="h-full left-0 top-0 w-full">
              <div
                className={`wistia_embed wistia_async_${videoId} videoFoam=true muted=true autoPlay=${
                  autoplay === true ? 'true' : 'false'
                } playbar=false qualityControl=false settingsControl=false smallPlayButton=false fullscreenButton=false silentAutoPlay=allow googleAnalytics=false endVideoBehavior=reset w-full h-full relative`}
              >
                <div
                  className={cx(
                    'wistia_swatch h-full left-0 opacity-0 overflow-hidden absolute top-0 [transition:opacity_.2s] w-full',
                    {
                      'opacity-100': !!isImageThumbnailLoaded,
                    }
                  )}
                >
                  <img
                    className="[filter:blur(5px)] h-full object-contain w-full"
                    src={`//fast.wistia.com/embed/medias/${videoId}/swatch`}
                    alt=""
                    aria-hidden="true"
                    onLoad={() => {
                      setIsImageThumbnailLoaded(true);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  );
};

SingleHeroVideoWistia.propTypes = {
  autoplay: PropTypes.bool,
  isAdmin: PropTypes.bool,
  videoId: PropTypes.string,
};

const SingleHeroVideoVimeo = ({
  videoId = 'https://vimeo.com/manage/videos/560538232 ',
  autoplay = false,
}) => {
  // If video URL was copied while logged into Vimeo, it may be passed into the
  // component in the format https://vimeo.com/manage/videos/560538232 but the
  // only format supported by the Vimeo player is https://vimeo.com/560538232
  const vimeoVideoId = videoId
    ? videoId.replace('/manage/videos', '')
    : videoId;

  return (
    !!videoId && (
      <div className="DualHeroVideoCore">
        <div className="px-0 py-[56.25%0] relative">
          <div className="h-full left-0 top-0 w-full">
            <ReactVimeo
              className="SingleHeroVideo__video--vimeo"
              video={vimeoVideoId}
              responsive
              autoplay={autoplay}
              showTitle={false}
            />
          </div>
        </div>
      </div>
    )
  );
};

SingleHeroVideoVimeo.propTypes = {
  videoId: PropTypes.string,
  autoplay: PropTypes.bool,
};

const DualVideoSet = ({ config: { videoBlock1, videoBlock2 }, isAdmin }) => {
  const isAnyTitleVisible =
    videoBlock1?.heading?.displayVideoTitle?.enabled === 'Show' ||
    videoBlock2?.heading?.displayVideoTitle?.enabled === 'Show';
  return (
    <FixedWidthContainer className="min-h-[100px]">
      <DesktopColumnsMobileStack>
        <div className="relative overflow-hidden">
          {isAnyTitleVisible ? (
            <div className="w-full text-left font-sans text-[1.2rem] font-bold tracking-[-1px] leading-[2rem] text-gray min-h-[2rem]">
              {videoBlock1?.heading?.displayVideoTitle?.enabled === 'Show'
                ? videoBlock1?.heading?.content
                : ''}
            </div>
          ) : null}
          {!!videoBlock1?.videoProps?.videoID &&
          videoBlock1?.videoProps?.videoID.startsWith('http') !== true ? (
            <SingleHeroVideoWistia
              videoId={videoBlock1?.videoProps?.videoID}
              isAdmin={isAdmin}
              autoplay={videoBlock1?.videoProps?.autoplay?.enabled === 'Show'}
            />
          ) : (
            <SingleHeroVideoVimeo
              videoId={videoBlock1?.videoProps?.videoID}
              autoplay={videoBlock1?.videoProps?.autoplay?.enabled === 'Show'}
            />
          )}
        </div>

        <div className="relative overflow-hidden">
          {isAnyTitleVisible ? (
            <div className="w-full text-left font-sans text-[1.2rem] font-bold tracking-[-1px] leading-[2rem] text-gray min-h-[2rem]">
              {videoBlock2?.heading?.displayVideoTitle?.enabled === 'Show'
                ? videoBlock2?.heading?.content
                : ''}
            </div>
          ) : null}
          {!!videoBlock2?.videoProps?.videoID &&
          videoBlock2?.videoProps?.videoID.startsWith('http') !== true ? (
            <SingleHeroVideoWistia
              videoId={videoBlock2?.videoProps?.videoID}
              isAdmin={isAdmin}
              autoplay={videoBlock2?.videoProps?.autoplay?.enabled === 'Show'}
            />
          ) : (
            <SingleHeroVideoVimeo
              videoId={videoBlock2?.videoProps?.videoID}
              autoplay={videoBlock2?.videoProps?.autoplay?.enabled === 'Show'}
            />
          )}
        </div>
      </DesktopColumnsMobileStack>
    </FixedWidthContainer>
  );
};
DualVideoSet.propTypes = {
  config: PropTypes.shape({
    videoBlock1: PropTypes.shape({
      heading: PropTypes.shape({
        displayVideoTitle: PropTypes.shape({
          enabled: PropTypes.string,
        }),
        content: PropTypes.string,
      }),
      videoProps: PropTypes.shape({
        autoplay: PropTypes.string,
        videoID: PropTypes.string,
      }),
    }),
    videoBlock2: PropTypes.shape({
      heading: PropTypes.shape({
        displayVideoTitle: PropTypes.shape({
          enabled: PropTypes.string,
        }),
        content: PropTypes.string,
      }),
      videoProps: PropTypes.shape({
        autoplay: PropTypes.string,
        videoID: PropTypes.string,
      }),
    }),
  }),
  isAdmin: PropTypes.bool,
};

export default DualVideoSet;
